import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

/**
 * Sidebar configuration options
 */
export interface SidebarConfig {
	/** Default expanded state */
	defaultExpanded?: boolean;
	/** Width when expanded (in pixels) */
	expandedWidth?: number;
	/** Width when collapsed (in pixels) */
	collapsedWidth?: number;
	/** Whether to persist state in localStorage */
	persistInLocalStorage?: boolean;
}

/**
 * Sidebar state interface
 */
export interface SidebarState {
	/** Whether the sidebar is currently expanded */
	isExpanded: boolean;
	/** Configuration options */
	config: Required<SidebarConfig>;
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: Required<SidebarConfig> = {
	defaultExpanded: true,
	expandedWidth: 228,
	collapsedWidth: 80,
	persistInLocalStorage: false,
};

/**
 * Get initial state from localStorage if persistence is enabled
 */
const getInitialState = (config: SidebarConfig = {}): SidebarState => {
	const mergedConfig = { ...DEFAULT_CONFIG, ...config };
	
	let initialExpanded = mergedConfig.defaultExpanded;
	
	if (mergedConfig.persistInLocalStorage) {
		try {
			const stored = localStorage.getItem("sidebar:isExpanded");
			if (stored !== null) {
				initialExpanded = stored === "true";
			}
		} catch (error) {
			// Silently fail if localStorage is not available
			console.warn("Failed to read sidebar state from localStorage:", error);
		}
	}
	
	return {
		isExpanded: initialExpanded,
		config: mergedConfig,
	};
};

/**
 * Create sidebar slice with optional configuration
 */
export const createSidebarSlice = (config: SidebarConfig = {}) => {
	const initialState = getInitialState(config);
	
	const slice = createSlice({
		name: "sidebar",
		initialState,
		reducers: {
			/**
			 * Toggle sidebar expanded state
			 */
			toggleSidebar(state) {
				state.isExpanded = !state.isExpanded;
				
				// Persist to localStorage if enabled
				if (state.config.persistInLocalStorage) {
					try {
						localStorage.setItem("sidebar:isExpanded", String(state.isExpanded));
					} catch (error) {
						console.warn("Failed to save sidebar state to localStorage:", error);
					}
				}
			},
			
			/**
			 * Set sidebar expanded state explicitly
			 */
			setSidebarExpanded(state, action: PayloadAction<boolean>) {
				state.isExpanded = action.payload;
				
				// Persist to localStorage if enabled
				if (state.config.persistInLocalStorage) {
					try {
						localStorage.setItem("sidebar:isExpanded", String(state.isExpanded));
					} catch (error) {
						console.warn("Failed to save sidebar state to localStorage:", error);
					}
				}
			},
			
			/**
			 * Update sidebar configuration
			 */
			updateSidebarConfig(state, action: PayloadAction<Partial<SidebarConfig>>) {
				state.config = { ...state.config, ...action.payload };
			},
		},
	});
	
	return slice;
};

/**
 * Default sidebar slice instance
 * Use this if you don't need custom configuration
 */
const defaultSidebarSlice = createSidebarSlice();

export const { toggleSidebar, setSidebarExpanded, updateSidebarConfig } = defaultSidebarSlice.actions;
export default defaultSidebarSlice.reducer;
